import { useCallback, useState } from 'react';
import { adminAPI } from '../api/admin';
import { useToast } from '../contexts/ToastContext';

export const useAdminOperations = () => {
  const [isLoading, setIsLoading] = useState(false);
  const { showToast } = useToast();

  const withLoading = useCallback(
    async <T>(operation: () => Promise<T>, successMessage?: string, errorMessage?: string): Promise<T | null> => {
      setIsLoading(true);
      try {
        const result = await operation();
        if (successMessage) {
          showToast(successMessage, 'success');
        }
        return result;
      } catch (error) {
        showToast(errorMessage || (error instanceof Error ? error.message : 'Operation failed'), 'error');
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [showToast],
  );

  const loadAdminData = useCallback(async () => {
    return withLoading(
      async () => {
        const [users, settings] = await Promise.all([adminAPI.getUsers(), adminAPI.getSettings()]);
        return { users, settings };
      },
      undefined,
      'Failed to load admin data',
    );
  }, [withLoading]);

  const createUser = useCallback(
    async (userData: { username: string; password: string; role: string }) => {
      return withLoading(() => adminAPI.createUser(userData), 'User created successfully');
    },
    [withLoading],
  );

  const updateUsername = useCallback(
    async (userId: string, username: string) => {
      return withLoading(() => adminAPI.updateUsername(userId, username), 'Username updated successfully');
    },
    [withLoading],
  );

  const updatePassword = useCallback(
    async (userId: string, password: string) => {
      return withLoading(() => adminAPI.updatePassword(userId, password), 'Password updated successfully');
    },
    [withLoading],
  );

  const deleteUser = useCallback(
    async (userId: string) => {
      return withLoading(() => adminAPI.deleteUser(userId), 'User deleted successfully');
    },
    [withLoading],
  );

  const updateSystemSetting = useCallback(
    async (key: string, value: unknown, description?: string) => {
      return withLoading(() => adminAPI.updateSystemSetting(key, value, description), 'System setting updated successfully');
    },
    [withLoading],
  );

  return {
    isLoading,
    loadAdminData,
    createUser,
    updateUsername,
    updatePassword,
    deleteUser,
    updateSystemSetting,
    showToast,
  };
};
